import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { zhHK } from './locales/zh-HK';
import { en } from './locales/en';
import { zhCN } from './locales/zh-CN';

/**
 * CoSingery — i18n 初始化（三語：zh-HK 預設／en／zh-CN）
 *
 * ⭐ 兩層守衛（**缺 key 一定 build 紅**，唔准靜靜回 undefined）：
 *   ① 型別層：下面兩行 `const _parityX: typeof zhHK = …`
 *      → 缺 key／型別唔同 ＝ `npm run typecheck` 即刻報錯（build 已包含 typecheck）
 *   ② 機器層：`npm run check:locales`（用 vendored `@coeldery/parity-guard`）
 *      → 額外咬「多餘 key／空值／型別不符」，亦已包含喺 `npm run build`
 *
 * ⚠️ 一律以 **zh-HK 為基準**（唔可以反向寫 `typeof en = zhHK` ✗）
 * ⚠️ 加新 key 一定三語齊（`src/locales/{zh-HK,en,zh-CN}/index.ts`）
 */
const _parityEn: typeof zhHK = en;
const _parityZhCn: typeof zhHK = zhCN;
void _parityEn;
void _parityZhCn;

export const SUPPORTED_LOCALES = ['zh-HK', 'en', 'zh-CN'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'zh-HK';

export const resources = {
  'zh-HK': { translation: zhHK },
  en: { translation: en },
  'zh-CN': { translation: zhCN },
} as const;

const STORAGE_KEY = 'cosingery.locale';

function pickInitialLocale(): SupportedLocale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as SupportedLocale;
    }
  } catch {
    /* 私隱模式可能停用 localStorage → 照用預設 */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : DEFAULT_LOCALE;
  if (nav.toLowerCase().startsWith('zh-cn') || nav.toLowerCase().startsWith('zh-hans')) return 'zh-CN';
  if (nav.toLowerCase().startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

void i18next.use(initReactI18next).init({
  resources,
  lng: pickInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
});

i18next.on('languageChanged', (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* 忽略：只係記住偏好，唔影響功能 */
  }
});

/** 切換語言（只准傳支援嘅語言；UI 唔准自己寫語言字串） */
export function changeLocale(locale: SupportedLocale): void {
  void i18next.changeLanguage(locale);
}

export default i18next;
